import { Action } from '@reduxjs/toolkit'
import {
  AddressStore,
  addressRepository,
  addressStore
} from '@entities/address'
import { StateObservable } from 'redux-observable'
import { of, filter, Observable, from, concat, tap, EMPTY } from 'rxjs'
import {
  catchError,
  switchMap,
  withLatestFrom,
  debounceTime,
  finalize
} from 'rxjs/operators'
import { registerEpic } from '@shared/lib/redux'
import {
  ToastsStore,
  toastListStore
} from '@shared/lib/redux-toast/model/redux-toast.slice'

type PartialRootState = {
  [addressStore.name]: AddressStore
  [toastListStore.name]: ToastsStore
}

const getCityListEpic = (action$: Observable<Action>) =>
  action$.pipe(
    filter(addressStore.actions.getCityListStart.match),
    switchMap((action) => {
      const abortController = new AbortController()
      return from(
        addressRepository.getCityList({
          page: action.payload.page,
          cityPart: action.payload.cityPart,
          abortSignal: abortController.signal
        })
      ).pipe(
        switchMap((response) =>
          concat(
            of(
              addressStore.actions.getCityListSuccess({
                items: response.data,
                meta: {
                  page: response.meta.page,
                  size: response.meta.size,
                  total: response.meta.total
                }
              })
            )
          )
        ),
        catchError((error) =>
          concat(
            of(addressStore.actions.getCityListFailure(error as string))
            // of(
            //   toastListStore.actions.addToast({
            //     type: 'error',
            //     message: error
            //   })
            // )
          )
        ),
        finalize(() => abortController.abort())
      )
    })
  )

const getAddressListEpic = (
  action$: Observable<Action>,
  state$: StateObservable<PartialRootState>
) =>
  action$.pipe(
    filter(addressStore.actions.getAddressList.match),
    debounceTime(500),
    withLatestFrom(state$),
    switchMap(([action, _state]) => {
      const abortController = new AbortController()
      return from(
        addressRepository.getAddressList({
          addressPart: action.payload.addressPart,
          city: action.payload.city,
          page: action.payload.page,
          size: action.payload.size,
          abortSignal: abortController.signal
        })
      ).pipe(
        switchMap((response) =>
          concat(
            of(
              addressStore.actions.getAddressListSuccess({
                items: response.data,
                meta: {
                  page: response.meta.page,
                  size: response.meta.size,
                  total: response.meta.total
                }
              })
            )
          )
        ),
        catchError((error) =>
          concat(
            of(addressStore.actions.getAddressListFailure(error as string)),
            of(
              toastListStore.actions.addToast({
                type: 'error',
                message: error
              })
            )
          )
        ),
        finalize(() => abortController.abort())
      )
    })
  )

const setCurrentCityEpic = (action$: Observable<Action>) =>
  action$.pipe(
    filter(addressStore.actions.setCurrentCity.match),
    tap((action) => {
      return addressRepository.saveCity(action.payload)
    }),
    switchMap(() => EMPTY)
  )

registerEpic(getCityListEpic)
registerEpic(getAddressListEpic)
registerEpic(setCurrentCityEpic)
